import * as React from 'react';
import { Controller, type SubmitHandler } from 'react-hook-form';
import { NICKNAME_REGEXP } from '@plarium/gpd-validation-rules';
import { WidgetId } from '@/edge/constants';
import { PROFILE_EDIT_FORM_FIELDS } from '@/modules/profile/constants';
import { useMutationUpdateUserData } from '@/modules/profile/hooks/use-mutation-update-user-data';
import { useProfileContext } from '@/modules/profile/hooks/use-profile-context';
import { useProfileDataQuery } from '@/modules/profile/hooks/use-profile-data-query';
import { useProfileEditForm } from '@/modules/profile/hooks/use-profile-edit-form';
import type { ProfileEditFormValues } from '@/modules/profile/types';
import { Button } from '@/shared/components/button';
import { useEdgeConfigWidget } from '@/shared/modules/edge-config';
import { buildQaTrackingEntityProps } from '@/shared/modules/qa-tracking-entity';
import { TextInput } from '../text-input';
import { Textarea } from '../textarea';
import * as Styled from './profile-edit-form.styled';
const ABOUT_MAX_LENGTH = 360;
export const ProfileEditForm = React.memo(function ProfileEditForm() {
  const profileModel = useEdgeConfigWidget(WidgetId.Profile);
  const {
    data
  } = useProfileDataQuery();
  const {
    setIsEditMode
  } = useProfileContext();
  const {
    formMethods
  } = useProfileEditForm();
  const {
    mutate,
    isPending
  } = useMutationUpdateUserData(formMethods);
  const onSubmit: SubmitHandler<ProfileEditFormValues> = ({
    nickname,
    about
  }) => {
    mutate({
      nickname,
      about
    });
  };
  return <form onSubmit={formMethods.handleSubmit(onSubmit)}>
      <Styled.FormFieldsWrapper>
        <Controller name={PROFILE_EDIT_FORM_FIELDS.NICKNAME} control={formMethods.control} rules={{
        required: {
          value: true,
          message: profileModel.strings.errorRequired
        },
        pattern: {
          value: NICKNAME_REGEXP,
          message: profileModel.strings.errorNickname
        }
      }} render={({
        field
      }) => <TextInput value={field.value} placeholder={profileModel.strings.nickNamePlaceholder} label={profileModel.strings.nickName} disabled={data?.isLockedChangeName} errorMessage={formMethods.formState.errors.nickname?.message} onChange={field.onChange} onBlur={field.onBlur} {...buildQaTrackingEntityProps('newProfile.profileEditModal.input.nickname')} errorQaEntity={buildQaTrackingEntityProps('newProfile.profileEditModal.error.nickname')} />} />

        <Controller name={PROFILE_EDIT_FORM_FIELDS.ABOUT} control={formMethods.control} rules={{
        maxLength: {
          value: ABOUT_MAX_LENGTH,
          message: profileModel.strings.errorAbout
        }
      }} render={({
        field
      }) => <Textarea value={field.value} placeholder={profileModel.strings.aboutMePlaceholder} errorMessage={formMethods.formState.errors.about?.message} label={profileModel.strings.aboutMe} onChange={field.onChange} onBlur={field.onBlur} {...buildQaTrackingEntityProps('newProfile.profileEditModal.input.aboutMe')} />} />
      </Styled.FormFieldsWrapper>

      <Styled.ButtonsHolder>
        <Button type="button" size="m" onClick={() => setIsEditMode(false)} {...buildQaTrackingEntityProps('newProfile.profileEditModal.input.cancel')}>
          {profileModel.strings.cancel}
        </Button>
        <Button type="submit" size="m" color="secondary" disabled={!formMethods.formState.isDirty || isPending} {...buildQaTrackingEntityProps('newProfile.profileEditModal.input.confirm')}>
          {profileModel.strings.confirm}
        </Button>
      </Styled.ButtonsHolder>
    </form>;
});